import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, PenLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/app-shell.tsx';
import { Panel, Pill, type Tone } from '../components/ui.tsx';
import { type PublicationStatus, publicationsApi, sitesApi } from '../lib/api.ts';

const STATUS_TONE: Record<PublicationStatus, Tone> = {
  scheduled: 'info',
  processing: 'info',
  completed: 'success',
  partial: 'warning',
  failed: 'danger',
  canceled: 'neutral',
};

/** "Hoy" en la zona de quien mira, que es como lo piensa una redacción. */
function isToday(iso: string): boolean {
  return new Date(iso).toDateString() === new Date().toDateString();
}

/**
 * Panel de inicio (§10.6).
 *
 * Responde, por este orden, a lo que alguien quiere saber al abrir el panel por
 * la mañana: ¿hay algo roto que tenga que atender?, ¿qué salió hoy?, y —lo más
 * frecuente— "quiero escribir una nota".
 *
 * Enseña lo que hay de verdad. Un bloque de avisos vacío no aparece: una
 * tarjeta que dice "0 fallos" en grande le quita sitio a lo que sí importa y
 * acostumbra a no mirarla.
 */
export function DashboardPage() {
  const { t } = useTranslation();

  const { data: sitesData } = useQuery({ queryKey: ['sites'], queryFn: sitesApi.list });
  const { data: publicationsData } = useQuery({
    queryKey: ['publications'],
    queryFn: publicationsApi.list,
    refetchInterval: 15_000,
  });

  const sites = sitesData?.sites ?? [];
  const publications = publicationsData?.publications ?? [];

  const sitesNeedingAttention = sites.filter((site) => !['active', 'disabled'].includes(site.status));
  const failing = publications.filter((entry) => entry.status === 'failed' || entry.status === 'partial');
  const today = publications.filter((entry) => isToday(entry.createdAt));
  const publishedToday = today.filter((entry) => entry.status === 'completed').length;
  const inFlight = publications.filter((entry) => ['processing', 'scheduled'].includes(entry.status)).length;

  const composeLink = (
    <Link
      to="/redactar"
      className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-brand)] px-4 py-2.5 text-sm font-medium text-white shadow-[0_1px_2px_rgb(43_78_255/0.35)] hover:bg-[var(--color-brand-strong)]"
    >
      <PenLine size={16} aria-hidden="true" />
      {t('dashboard.compose')}
    </Link>
  );

  // Sin periódicos conectados no hay nada que resumir: lo útil es decir por
  // dónde se empieza, no enseñar cuatro ceros.
  if (sitesData && sites.length === 0) {
    return (
      <AppShell title={t('dashboard.title')}>
        <Panel className="p-8">
          <h2 className="font-display text-xl font-semibold">{t('dashboard.start.title')}</h2>
          <ol className="mt-5 space-y-4">
            {(['connect', 'categories', 'write'] as const).map((step, index) => (
              <li key={step} className="flex gap-4">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-brand-soft)] text-sm font-medium text-[var(--color-brand-strong)]">
                  {index + 1}
                </span>
                <p className="pt-0.5 text-sm leading-relaxed">{t(`dashboard.start.${step}`)}</p>
              </li>
            ))}
          </ol>
          <Link
            to="/sitios"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[var(--color-brand)] px-4 py-2.5 text-sm font-medium text-white hover:bg-[var(--color-brand-strong)]"
          >
            {t('dashboard.start.cta')}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </Panel>
      </AppShell>
    );
  }

  const stats = [
    { label: t('dashboard.stats.publishedToday'), value: publishedToday, to: '/publicaciones' },
    { label: t('dashboard.stats.inFlight'), value: inFlight, to: '/publicaciones' },
    { label: t('dashboard.stats.sites'), value: sites.length, to: '/sitios' },
  ] as const;

  return (
    <AppShell title={t('dashboard.title')} actions={composeLink}>
      {/* --- Lo que pide acción, antes que nada ------------------------------ */}
      {failing.length > 0 || sitesNeedingAttention.length > 0 ? (
        <Panel className="mb-6 border-[var(--color-danger)]/30">
          <h2 className="border-b border-[var(--color-line)] px-5 py-3 text-sm font-medium text-[var(--color-danger)]">
            {t('dashboard.attention.title')}
          </h2>
          <ul className="divide-y divide-[var(--color-line)]">
            {failing.slice(0, 5).map((entry) => (
              <li key={entry.id}>
                <Link
                  to="/publicaciones/$id"
                  params={{ id: entry.id }}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--color-surface)]"
                >
                  <Pill tone={STATUS_TONE[entry.status]}>{t(`publicationStatus.${entry.status}`)}</Pill>
                  <span className="min-w-0 flex-1 truncate text-sm">{entry.articleTitle}</span>
                  <ArrowRight size={16} aria-hidden="true" className="shrink-0 text-[var(--color-muted)]" />
                </Link>
              </li>
            ))}
            {sitesNeedingAttention.map((site) => (
              <li key={site.id}>
                <Link
                  to="/sitios"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--color-surface)]"
                >
                  <Pill tone="warning">{t('dashboard.attention.site')}</Pill>
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {site.name}
                    {site.lastErrorMessage ? (
                      <span className="text-[var(--color-muted)]"> — {site.lastErrorMessage}</span>
                    ) : null}
                  </span>
                  <ArrowRight size={16} aria-hidden="true" className="shrink-0 text-[var(--color-muted)]" />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {/* --- Cifras del día -------------------------------------------------- */}
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        {stats.map((stat) => (
          <Link
            key={stat.label}
            to={stat.to}
            className="rounded-xl border border-[var(--color-line)] bg-[var(--color-card)] p-5 transition hover:border-[var(--color-brand)]"
          >
            <p className="font-display text-4xl font-semibold tabular-nums">{stat.value}</p>
            <p className="mt-1 text-sm text-[var(--color-muted)]">{stat.label}</p>
          </Link>
        ))}
      </div>

      {/* --- Últimas publicaciones ------------------------------------------ */}
      <Panel>
        <div className="flex items-center justify-between border-b border-[var(--color-line)] px-5 py-3">
          <h2 className="text-sm font-medium">{t('dashboard.recent.title')}</h2>
          <Link to="/publicaciones" className="text-sm text-[var(--color-brand)] hover:underline">
            {t('dashboard.recent.all')}
          </Link>
        </div>

        {publications.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-[var(--color-muted)]">
            {t('dashboard.recent.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-[var(--color-line)]">
            {publications.slice(0, 6).map((entry) => (
              <li key={entry.id}>
                <Link
                  to="/publicaciones/$id"
                  params={{ id: entry.id }}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--color-surface)]"
                >
                  <Pill tone={STATUS_TONE[entry.status]}>{t(`publicationStatus.${entry.status}`)}</Pill>
                  <span className="min-w-0 flex-1 truncate font-display text-base">{entry.articleTitle}</span>
                  <time dateTime={entry.createdAt} className="shrink-0 text-xs text-[var(--color-muted)]">
                    {new Date(entry.createdAt).toLocaleString('es-DO', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </time>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </AppShell>
  );
}
